import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Assignment from "@material-ui/icons/Assignment";
import update from "immutability-helper";
import * as React from "react";
import { RouterProps } from "react-router";
import { Link } from "react-router-dom";
import {
  IProperPostResponse,
  IQuestionRequest,
  ISanitizedSurveyQuestion,
  ISurveyRespondentRequest,
  ISurveyRespondentResponse,
  ResponseSource,
  TemplateQuestionCategory,
  TemplateQuestionType,
} from "src/services";
import Component from "../../components/Component";
import MobilePage from "../../components/MobilePage";
import withAxios, { IAxiosProps } from "../../components/withAxios";
import {
  IFinishedSurveySection,
  IRespondentPagePassedDown,
} from "../RespondentPage";
import Question from "./surveyRespondPage/Question";
import { Box } from "@material-ui/core";
import { hexToRGB } from "src/util";

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  button: {
    padding: "15px 25px 15px 25px",
  },
  buttonDiv: {
    marginTop: "2rem",
    textAlign: "right",
  },
  categoryType: {
    fontSize: 26,
  },
  disclaimerText: {
    color: "black",
    fontSize: "18px",
  },
  heading: {
    color: "#686868",
    fontSize: "20px",
    marginBottom: "2rem",
  },
  questionWrapper: {
    backgroundColor: `rgba(${hexToRGB(theme.palette.primary.main)}, 0.1)`,
    borderRadius: "4px",
    padding: "1.5rem 1rem",
  },
});

interface ISurveyRespondPageProps {
  code: string;
  coachId: number | undefined;
  category: number;
  collectRespondentName: boolean;
  section: number;
  finishedSurveySectionPush: (
    finishedSurveySection: IFinishedSurveySection
  ) => void;
  respondentName: string;
  forVideoMidSeson: boolean;
   schoolData:any;
}

type IProps = ISurveyRespondPageProps &
  IRespondentPagePassedDown &
  RouterProps &
  IAxiosProps &
  WithStyles<keyof ReturnType<typeof styles>>;

interface ISurveyRespondPageState {
  questionResponses: IQuestionRequest[]; // It is a request to the server that is why it is named like this
  respondentName: string;
  submitting?: true;
}

const QUESTION_SPLIT_MIN = 6;

export default withStyles(styles)(
  withAxios(
    class SurveyRespondPage extends Component<IProps, ISurveyRespondPageState> {
      public constructor(props: IProps) {
        super(props);
        this.state = {
          questionResponses: ((): IQuestionRequest[] => {
            if (props.surveyResponse !== undefined) {
              return this.convertQuestions(props.surveyResponse.questions);
            } else {
              return [];
            }
          })(),
          respondentName: "",
        };
      }

      public componentDidMount() {
        const {
          collectRespondentName,
          loadSurvey,
          code,
          history,
          category,
          coachId,
          responseSource,
          respondentName,
          section,
        } = this.props;
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];
        loadSurvey(code);

        // if respondentName is '' and we need to collect respondent names, go collect the name
        if (collectRespondentName && respondentName === "") {
          history.push(
            `/${responsePath}/${code}/${coachId}/inputNamePage`
          );
          return;
        }
        // If user refreshes at end of page bring them back
        // console.log(category, this.getCategoryLength());
        else if (category === this.getCategoryLength()) {
          // bring user back to coach select page
          history.push(`/${responsePath}/${code}`);
        } else if (category !== 0 || section !== 0) {
          // bring user back to start of survey
          history.push(
            `/${responsePath}/${code}/${coachId}/${0}`
          );
        }
      }

      public componentDidUpdate(prevProps: IProps) {
        const {
          surveyResponse,
          responseSource,
          history,
          coachId,
          code,
          finishedSurveySections,
          category,
        } = this.props;
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];

        // if there are no questions in the current category, skip ahead to the next category.
        if (
          surveyResponse !== undefined &&
          surveyResponse.questions.find(
            (question) => question.category === category
          ) === undefined
        ) {
          if (category < this.getCategoryLength()) {
            history.push(
              `/${responsePath}/${code}/${coachId}/${
                category + 1
              }`
            );
          }
        }
        // make the user return to the coach select if the coach page they are at
        // is already marked as done.
        if (
          finishedSurveySections.find(
            (fS) =>
              fS.code === code &&
              fS.coachId === coachId &&
              category < this.getCategoryLength()
          )
        ) {
          history.push(`/${responsePath}/${code}`);
        }

        // check if a coach is in the coachs list. If not then send the user back
        if (
          surveyResponse !== undefined &&
          !surveyResponse.coaches.find((c) => c.id === coachId)
        ) {
          history.push(`/${responsePath}/${code}`);
        }

        this.setNewQuestionsState(prevProps);
      }

      // render for both students and coaches
      public render() {
        const {
          responseSource,
          surveyResponse,
          loadingSurvey,
          category,
          coachId,
          school,
          classes,
          forVideoMidSeson
        } = this.props;
        if (surveyResponse === undefined) {
          if (loadingSurvey) {
            return <LinearProgress variant="query" />;
          }
          return <h1>Oops... something went wrong. Survey not loaded.</h1>;
        }
        const coach = surveyResponse.coaches.find((c) => c.id === coachId);
        if (coach === undefined) {
          return <h1>Oops... something went wrong. {this.props.schoolData.isAdSurvey ? "AD" : "Teacher"} was not found.</h1>;
        }

        const sportName = (() => {
          if (
            surveyResponse === undefined ||
            surveyResponse.sport === undefined
          ) {
            return ``;
          } else {
            return `${surveyResponse.sport.name}`;
          }
        })();

        // const firstToUpper = (s: string): string =>
        //   s === undefined ? "" : s[0].toUpperCase() + s.substring(1);

        const unfinishedSurvey = category < this.getCategoryLength();

        // Dynamic headline logic: Expectation, Warmth, Written Response for coach survey
        const headline = (() => {
          if (responseSource === ResponseSource.coach) {
            switch (category) {
              case TemplateQuestionCategory.expectations:
                return 'Expectations';
              case TemplateQuestionCategory.warmth:
                return 'Warmth';
              case TemplateQuestionCategory.other:
                return 'Written Responses';
              default:
                return '';
            }
          } else {
            // For other survey types, keep previous logic or fallback
            switch (category) {
              case TemplateQuestionCategory.expectations:
                return 'Expectations';
              case TemplateQuestionCategory.warmth:
                return 'Warmth';
              case TemplateQuestionCategory.other:
                return 'Written Responses';
              default:
                return '';
            }
          }
        })();


        const survey = unfinishedSurvey
          ? this.renderSurveyCategory()
          : this.renderFinishedPage(surveyResponse);
                           console.log("headline",headline)
         
        switch (responseSource) {
          case ResponseSource.student:
            return (
              <MobilePage
                header={sportName}
                title={`${this.props.schoolData.isAdSurvey ? "AD:" :"Teacher:"} ${coach.name}`}
                subtitle=""
                schoolLogo={school.logo1}
                schoolLogo2={school.logo2}
                schoolName={school.name}
                headerIcon={<Assignment />}
                tagline={`${school.tagline}`}
                headLine= {headline}
                type="Student"
                forVideoMidSeson={forVideoMidSeson}
              >
                 
                <Typography variant="subtitle1" className={classes.heading}>
                  {headline}
                </Typography>
                {survey}
              </MobilePage>
            );
          case ResponseSource.coach:
            return (
              <MobilePage
                header={sportName}
                title={`${this.props.schoolData.isAdSurvey ? "AD" : "Teacher"
}: ${coach.name}`}
                subtitle="Self-survey is imperative for great leaders."
                schoolLogo={school.logo1}
                schoolLogo2={school.logo2}
                schoolName={school.name}
                headerIcon={<Assignment />}
                tagline={`${school.tagline}`}
                headLine= {headline}
                type= "Coach"
              >
                <Typography variant="subtitle1" className={classes.heading}>
                  {headline}
                </Typography>
                {survey}
              </MobilePage>
            );
          case ResponseSource.parent:
            return (
              <MobilePage
                header={sportName}
                title={`${this.props.schoolData.isAdSurvey ? "AD:" :"Teacher:"} ${coach.name}`}
                subtitle=""
                schoolLogo={school.logo1}
                schoolLogo2={school.logo2}
                schoolName={school.name}
                headerIcon={<Assignment />}
                tagline={`${school.tagline}`}
                headLine= {headline}
                type= "Parent"
              >

                <Typography variant="subtitle1" className={classes.heading}>
                  {headline}
                </Typography>
                {survey}
              </MobilePage>
            );
          default:
            return <span>Something is wrong</span>;
        }
      }

      private renderFinishedPage(surveyResponse: ISurveyRespondentResponse) {
        const { responseSource, code, finishedSurveySections, coachId } =
          this.props;
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];
        const coach = surveyResponse.coaches.find((c) => c.id === coachId);
        if (coach === undefined) {
          return <span>Oops {this.props.schoolData.isAdSurvey ? "School Admin" : "Teacher"} not found</span>;
        }
        const finished =
          surveyResponse.coaches.filter(
            (c) =>
              !finishedSurveySections.find(
                (fS) => fS.coachId === c.id && fS.code === code
              )
          ).length === 0;
        return (
          <div>
            <br />
            {(() => {
              if (
                !finished &&
                (responseSource === ResponseSource.student ||
                  responseSource === ResponseSource.parent)
              ) {
                return (
                  <div>
                    <span>
                      Success! You have completed your survey about {this.props.schoolData.isAdSurvey ? "School Admin" : "Teacher"}{" "}
                      {coach.name}. <br />
                      <span style={{ padding: "0px 2px 0px 2px" }}>
                        <Button
                          color="primary"
                          component={Link}
                          {...({
                            to:`/${responsePath}/${code}`,
                          } as any)}
                        >
                          Start New {this.props.schoolData.isAdSurvey ? "School Admin" : "Teacher"} Survey
                        </Button>
                      </span>
                    </span>
                  </div>
                );
              }
              switch (responseSource) {
                case ResponseSource.student:
                  return (
                    <span>
                      Success! You have completed your survey about {this.props.schoolData.isAdSurvey ? "School Admin" : "Teacher"}{" "}
                      {coach.name}.
                    </span>
                  );
                case ResponseSource.coach:
                  return (
                    <span>Thanks {this.props.schoolData.isAdSurvey ? "School Admin" : "Teacher"}! Your answers have been recorded</span>
                  );
                case ResponseSource.parent:
                  return <span>Thanks! Your answers have been recorded</span>;
                default:
                  return <span>Oops... something went wrong</span>;
              }
            })()}
          </div>
        );
      }

      private renderSurveyCategory() {
        const {
          surveyResponse,
          loadingSurvey,
          category,
          classes,
          responseSource,
        } = this.props;
        const { submitting } = this.state;
        if (
          surveyResponse === undefined ||
          surveyResponse.survey === undefined
        ) {
          if (loadingSurvey) {
            return <LinearProgress variant="query" />;
          }
          return <h1>Oops... something went wrong</h1>;
        }

        const disclaimerText = (() => {
          switch (category) {
            case TemplateQuestionCategory.expectations:
              return "";
            case TemplateQuestionCategory.warmth:
              return "";
            case TemplateQuestionCategory.other:
              // different message for coaches vs students/parents
              if (responseSource === ResponseSource.coach) {
                return "Your comments will be used to shape the conversation toward the best course of action for your growth and development as a teacher.";
              } 
              // else {
              //   return "Confidentiality is important to us. We promise to keep your specific remarks confidential between us. You can trust that your comments will not be shared with your coach.";
              // }
            case TemplateQuestionCategory.parent:
              return "";
            default:
              return "";
          }
        })();

        // only render the questions of the category
        return (
          <Box>
            {category !== TemplateQuestionCategory.parent
              ? this.renderBackButton(category)
              : null}
            <Typography className={classes.disclaimerText}>
              {disclaimerText}         
            </Typography>
            {this.renderQuestions(category, surveyResponse.questions)}
            {submitting && <LinearProgress />}
            <Box className={classes.buttonDiv}>
              {this.renderButton(category, surveyResponse.questions)}
            </Box>
          </Box>
        );
      }

      private renderBackButton(category: number) {
        const { responseSource, code, coachId, section, surveyResponse } =
          this.props;
        const getEndPath = () => {
          if (section === 1) {
            return `${category}`;
          }
          const { splitSection } = this.getValidQuestions(category - 1);
          if (splitSection) {
            return `${category - 1}/1`;
          }
          return `${category - 1}`;
        };
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];

        const goBacklink = `/${responsePath}/${code}/${coachId}/${getEndPath()}`;

        return (
          <>
            {!!surveyResponse && category !== 0 && section !== 0 && (
              <IconButton aria-label="Back" component={Link} to={goBacklink}>
                <ArrowBack />
              </IconButton>
            )}
          </>
        );
      }

      // questionsCategoryIndex: -1 if question not in expectations, warmth, other, or parent. otherwise the number of the categoryIndex
      private getValidQuestions(inputCategory?: number) {
        const { surveyResponse, section, category: cat } = this.props;
        const category = inputCategory !== undefined ? inputCategory : cat;
        if (surveyResponse === undefined) {
          return {
            questionsCategoryIndex: [],
            splitSection: false,
          };
        }
        const categoryQuestionLength = surveyResponse.questions.filter(
          (q) => q.category === category
        ).length;
        // only split section if enough questions
        const splitSection = categoryQuestionLength >= QUESTION_SPLIT_MIN;

        const categoryStartIndexes = [0, 1, 2, 3].map((index) =>
          surveyResponse.questions.findIndex((q) => q.category === index)
        );

        const questionsCategoryIndex = surveyResponse.questions.map((q, i) => {
          const categoryIndex = i - categoryStartIndexes[q.category];
          const categorySectionFirst =
            categoryIndex >= Math.ceil(categoryQuestionLength / 2);
          if (
            q.category === category &&
            !(
              splitSection &&
              ((section === 0 && categorySectionFirst) ||
                (section === 1 && !categorySectionFirst))
            )
          ) {
            return categoryIndex;
          } else {
            return -1;
          }
        });
        return {
          questionsCategoryIndex,
          splitSection,
        };
      }

      private renderQuestions(
        category: number,
        questions: ISanitizedSurveyQuestion[]
      ) {     
        const { classes } = this.props;
        const { questionResponses } = this.state;
        const { questionsCategoryIndex } = this.getValidQuestions(
          this.props.category
        );
        return (
          <Box display="flex" flexDirection="column" gridGap="2.5rem">
            {questions.map((q, i) => {
              if (questionsCategoryIndex[i] === -1) {
                return null;
              }
              return (
                <Box key={q.id} className={classes.questionWrapper}>
                  <Question
                    questionResponse={questionResponses[i]}
                    question={q}
                    index={i}
                    categoryIndex={
                      !isNaN(questionsCategoryIndex[i])
                        ? questionsCategoryIndex[i]
                        : -1
                    }
                    handleResponseChange={this.handleResponseChange}
                  />
                </Box>
              );
            })}
          </Box>
        );
      }

      private handleResponseChange =
        (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
          // set value here to make it turn to a value instead of pointer
          const value = e.target.value;
          this.setState((prevState) =>
            update(prevState, {
              questionResponses: {
                [i]: { response: { $set: value } },
              },
            })
          );
        };

      private getCategoryLength = () => {
        return Object.keys(TemplateQuestionCategory).length / 2;
      };

      private disableButton = (): boolean => {
        const {
          // category,
          surveyResponse,
        } = this.props;
        const { questionResponses } = this.state;
        // disable button if the questions are not answered
        // add these variables to easily change what type of questions must be answered
        const ALLSCALE = true;
        const ALLWRITTEN = true;
        if (surveyResponse === undefined) {
          return false;
        }
        // filter questions responses that are in the category and section
        const { questionsCategoryIndex } = this.getValidQuestions();
        const categoryAndSectionQuestions = questionResponses.filter(
          (qR, i) => questionsCategoryIndex[i] !== -1
        );
        const scaleQuestions = categoryAndSectionQuestions.filter(
          (cQ) => cQ.type === TemplateQuestionType.scale
        );
        const writtenQuestions = categoryAndSectionQuestions.filter(
          (cQ) => cQ.type === TemplateQuestionType.written
        );
        const allScaleQuestionsAnswered = !Boolean(
          scaleQuestions.find((qR) => qR.response === "")
        );
        const allWrittenQuestionsAnswered = !Boolean(
          writtenQuestions.find((qR) => qR.response === "")
        );
        // console.log('scale', allScaleQuestionsAnswered, 'question', allWrittenQuestionsAnswered);
        if (ALLSCALE && ALLWRITTEN) {
          return !(allScaleQuestionsAnswered && allWrittenQuestionsAnswered);
        }
        if (ALLWRITTEN) {
          return !allWrittenQuestionsAnswered;
        }
        return !allScaleQuestionsAnswered;
      };

      private renderButton(
        category: number,
        questions: ISanitizedSurveyQuestion[]
      ) {
        const { responseSource, code, coachId, classes, section } = this.props;
        const responsePath =
          responseSource === ResponseSource.coach
            ? "teacher"
            : ResponseSource[responseSource];
        const { submitting, questionResponses } = this.state;
        const { questionsCategoryIndex, splitSection } =
          this.getValidQuestions();
        const moreQuestions =
          questionsCategoryIndex[questionsCategoryIndex.length - 1] === -1;
        const linkEnd = () => {
          if (splitSection && section === 0) {
            return `${category}/1`;
          }
          return `${category + 1}`;
        };
        if (!moreQuestions) {
          return (
            <Button
              disabled={
                submitting ||
                !questionResponses.find((qR) => qR.response !== "") ||
                this.disableButton()
              }
              color="primary"
              variant="contained"
              onClick={this.finishSurvey}
              className={classes.button}
            >
              Finish
            </Button>
          );
        } else {
          return (
            <div className="next-primary-btn-set">
            <Button
              disabled={this.disableButton()}
              color="primary"
              variant="contained"
              component={Link}
              onClick={this.nextButtonClick}
              {...({
                to: `/${responsePath}/${code}/${coachId}/${linkEnd()}`,
              } as any)}
              className={classes.button}
            >
              Next
            </Button>
            </div>
          );
        }
      }

      private nextButtonClick = () => {
        window.scroll(0, 0);
      };

      private setNewQuestionsState = (prevProps: IProps) => {
        const { surveyResponse } = this.props;
        // make sure things are not undefined for the next code
        if (
          surveyResponse === undefined ||
          surveyResponse.survey === undefined ||
          !surveyResponse.properAccess
        ) {
          // should not set the new data
          return;
        }

        if (
          this.state.questionResponses.length === 0 ||
          prevProps.surveyResponse === undefined ||
          prevProps.surveyResponse.survey === undefined ||
          surveyResponse.survey.code !== prevProps.surveyResponse.survey.code
        ) {
          this.setState({
            questionResponses: this.convertQuestions(surveyResponse.questions),
            respondentName: "",
          });
        }
      };

      // converts the question to the response parts.
      // because the questions are ordered properly it should be easy to
      private convertQuestions = (
        questionResponse: ISanitizedSurveyQuestion[]
      ): IQuestionRequest[] => {
        const { coachId } = this.props;
        if (coachId === undefined) {
          return [];
        }
        return questionResponse.map((qR) => ({
          response: "",
          surveyQuestionId: qR.id,
          type: qR.type,
        }));
      };

      private finishSurvey: () => undefined = () =>
        void this.runCancellableStatefulAsync(
          this,
          {
            submitting: true,
          },
          {
            submitting: undefined,
          },
          async () => {
            const { questionResponses } = this.state;
            const {
              surveyResponse,
              responseSource,
              coachId,
              history,
              finishedSurveySectionPush,
              respondentName,
            } = this.props;
            if (
              surveyResponse === undefined ||
              surveyResponse.survey === undefined
            ) {
              return;
            }
            const code = surveyResponse.survey.code;
            if (coachId === undefined) {
              return;
            }
            const properAccess = await this.buildPost<
              ISurveyRespondentRequest,
              IProperPostResponse
            >(
              this,
              `survey/respond/${responseSource}/${code}/${coachId}`
            )({
              questionResponses,
              respondentName,
            });
            if (properAccess.properAccessCode && properAccess.properDataSent) {
              const responsePath =
                responseSource === ResponseSource.coach
                  ? "teacher"
                  : ResponseSource[responseSource];
              history.push(
                `/${responsePath}/${code}/${coachId}/${this.getCategoryLength()}`
              );
              this.nextButtonClick();
              if (properAccess.finishedSurveySection) {
                finishedSurveySectionPush({ coachId, code });
              }
              // do something about this
              // history.push(`/${ResponseSource[responseSource]}/${code}`);
            } else {
              window.alert(properAccess.errorMessage);
            }
          },
          () => {
            this.setState({ submitting: undefined });
          }
        );
    }
  )
);



// WEBPACK FOOTER //
// ./src/pages/respondent/SurveyRespondPage.tsx